"use client";

import { PROVVAKT_MS } from "@/lib/klient-intervall";
import { arProvsida, provvaktTakt } from "@/lib/prov/provvakt-takt";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import { signaleraProvLage } from "@/lib/prov/prov-lage-signal";

// Provläges-vakten — den KLIENT-sidiga halvan av det exklusiva elevläget.
// Servern (proxy.ts, prov-lage-grind.ts) är den faktiska grinden; den här
// komponenten gör låset SYNLIGT för eleven själv. Runda 3 (2026-09-11): den
// tvingande "prov väntar"-popupen (N8) är BORTA — eleven startar provet
// själv från startsidan (MinaProvKort/ProvElevClient), utan att bli
// avbruten någon annanstans på sajten. Kvar:
//
// 1. "aktivt" (eleven har startat) → om eleven av något skäl inte redan är på
//    /prov-elev/[passId] (t.ex. proxyn hann inte, eller en gammal flik),
//    tvinga dit med router.replace — en backstop, inte den primära grinden.
// 2. 5-minuters-varning härledd ur slutTid, en gång per slutTid (ändras
//    slutTid — läraren förlänger provet — nollställs markören så varningen
//    kan visas igen för den nya tiden). Visas ENDAST när eleven redan är
//    inne i provet (/prov-elev/*) — aldrig som en popup någon annanstans.
//
// Adaptiv polling: glest (30 s) tills ett aktivt pass dyker upp, sedan tätt
// (6 s) medan det pågår — och alltid tätt på en provsida (/prov-elev/…, ej resultat), så att
// lärarens avslut når eleven direkt (vektr:prov-lage → ProvElevClient). Pollar aldrig för lärare/admin/skoladmin, aldrig
// under impersonation (matchar SEB:s och provläges-grindens egna undantag),
// och aldrig på /login (session finns inte där, och sidan renderas ofta
// utloggad).


interface AktivtData {
  passId: string;
  slutTid: string | null;
  mobilparningTillaten: boolean;
  lasplattaTillaten: boolean;
  /** Provinstallningar.inlast — av ⇒ eleven får lämna provsidan. */
  inlast: boolean;
  /** Vektr-lås: the student left the prov page and the prov is locked. */
  vektrLast?: boolean;
}

interface AktivtSvar {
  aktivt?: AktivtData;
  oppnat?: { passId: string };
}

export default function ProvVakt({
  roll,
  impersonerar,
}: {
  roll: string | null;
  impersonerar: boolean;
}) {
  const [femMinVarning, setFemMinVarning] = useState(false);
  // A started prov the student may leave (inlast off): shown as a banner with
  // the way back while she is somewhere else on vektr.se.
  const [pagaendePassId, setPagaendePassId] = useState<string | null>(null);
  const varnadForSlutTid = useRef<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  // usePathname changes identity every navigation — read the LATEST value
  // from a ref inside the poll loop instead of restarting the effect (and
  // thereby the poll timer) on every single client-side navigation.
  const aktivPathnameRef = useRef(pathname);
  aktivPathnameRef.current = pathname;
  // When the student came to the current page, for the poll's pace (provvakt-takt.ts).
  const provSidaSedanRef = useRef(0);
  // Set by the poll effect: poll now. Arriving on a prov page polls at once — the page's close
  // detection and Nav's pairing state wait for an answer, which may otherwise be 30 s away.
  const pollaNuRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    provSidaSedanRef.current = Date.now();
    if (arProvsida(pathname)) pollaNuRef.current?.();
  }, [pathname]);

  const skaPolla = roll === "elev" && !impersonerar && pathname !== "/login";

  useEffect(() => {
    if (!skaPolla) return;

    let cancelled = false;
    let timer: number | null = null;
    let abort: AbortController | null = null;
    // When an answer last named a pass (started or opened).
    let passNamndVid = 0;

    function hanteraFemMinVarning(aktivt: AktivtData) {
      if (!aktivPathnameRef.current.startsWith("/prov-elev/")) return;
      if (!aktivt.slutTid) return;
      const kvarMs = new Date(aktivt.slutTid).getTime() - Date.now();
      if (kvarMs > 0 && kvarMs <= 5 * 60 * 1000) {
        if (varnadForSlutTid.current !== aktivt.slutTid) {
          varnadForSlutTid.current = aktivt.slutTid;
          setFemMinVarning(true);
        }
      } else if (varnadForSlutTid.current !== aktivt.slutTid) {
        // Sluttiden ändrades (läraren förlängde provet) innan 5-minuters-
        // fönstret nåddes — nollställ markören så en ny varning kan visas
        // när DEN tiden i sin tur närmar sig.
        varnadForSlutTid.current = null;
      }
    }

    // Always replace the one pending timer, so a tick overtaken by onSynlig cannot leave a
    // second polling chain behind (pre-deploy review 2026-10-21).
    function schemalagg(ms: number) {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(tick, ms);
    }

    async function tick() {
      // A hidden tab neither needs the backstop redirect nor the 5-minute warning, and
      // every student has this poll on every page (prestanda-rattningslatens.md §10b P1b).
      // Becoming visible again polls at once (onSynlig below).
      if (document.hidden) {
        schemalagg(PROVVAKT_MS);
        return;
      }
      abort = new AbortController();
      let nastaDelayMs = PROVVAKT_MS;
      try {
        const res = await fetch("/api/prov-elev/aktivt", {
          cache: "no-store",
          signal: abort.signal,
        });
        if (cancelled) return;
        if (!res.ok) return;

        const data = (await res.json()) as AktivtSvar;

        // The pace (provvakt-takt.ts): tight while a pass runs, and on a prov page shortly after
        // it opened or a pass was last named; otherwise idle.
        const nu = Date.now();
        if (data.aktivt || data.oppnat) passNamndVid = nu;
        nastaDelayMs = provvaktTakt({
          sida: aktivPathnameRef.current,
          nu,
          // A student who may leave the prov and is elsewhere has nothing to hold her
          // there: the idle pace is enough for the banner (low footprint).
          aktivt:
            data.aktivt != null &&
            (data.aktivt.inlast !== false || arProvsida(aktivPathnameRef.current)),
          passNamndVid,
          sidaOppnadVid: provSidaSedanRef.current,
        });

        // Tell an open prov page which pass is live: if it is not its own, the teacher (or the
        // clock, or the student herself) has ended it and the page closes at once instead of at
        // the next save or the end of the time.
        signaleraProvLage({
          passId: data.aktivt?.passId ?? data.oppnat?.passId ?? null,
          aktivt: data.aktivt != null,
          // The QR button serves phone and tablet — forbidden only when the prov closes both.
          mobilTillaten: data.aktivt ? data.aktivt.mobilparningTillaten || data.aktivt.lasplattaTillaten : true,
          slutTid: data.aktivt ? data.aktivt.slutTid : null,
          inlast: data.aktivt ? data.aktivt.inlast !== false : undefined,
          vektrLast: data.aktivt ? data.aktivt.vektrLast === true : undefined,
        });

        setPagaendePassId(data.aktivt && data.aktivt.inlast === false ? data.aktivt.passId : null);
        if (data.aktivt) {
          // Backstop: eleven ska aldrig stå kvar utanför provsidan när
          // passet är aktivt och hon är inlåst. Proxyn/prov-lage-grind är den
          // faktiska grinden — det här täcker en klient som redan renderade
          // sidan innan grinden hann svara (t.ex. en gammal flik som vaknar).
          if (
            data.aktivt.inlast !== false &&
            !aktivPathnameRef.current.startsWith(`/prov-elev/${data.aktivt.passId}`)
          ) {
            router.replace(`/prov-elev/${data.aktivt.passId}`);
          }
          hanteraFemMinVarning(data.aktivt);
        }
      } catch {
        // Tyst — abort vid unmount, eller nätfel. Nästa tick försöker igen.
      } finally {
        if (!cancelled) schemalagg(nastaDelayMs);
      }
    }

    function onSynlig() {
      if (document.hidden || cancelled) return;
      if (timer !== null) window.clearTimeout(timer);
      if (abort) abort.abort();
      void tick();
    }
    document.addEventListener("visibilitychange", onSynlig);
    pollaNuRef.current = onSynlig;

    tick();
    return () => {
      pollaNuRef.current = null;
      cancelled = true;
      document.removeEventListener("visibilitychange", onSynlig);
      if (abort) abort.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [skaPolla, router]);

  if (!skaPolla || typeof document === "undefined") return null;

  if (femMinVarning) {
    return createPortal(
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="provvakt-femmin-rubrik"
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      >
        <div className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-xl">
          <h2
            id="provvakt-femmin-rubrik"
            className="mb-2 text-base font-semibold text-[var(--color-text)]"
          >
            5 minuter kvar av provtiden
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-[var(--color-text)]">
            Provet lämnas in automatiskt när tiden går ut.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setFemMinVarning(false)}
              title="Stäng notisen om att provtiden snart går ut"
              className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Not on the start page: its own prov cards already say "Fortsätt provet".
  if (pagaendePassId && pathname !== "/" && !pathname.startsWith("/prov-elev/")) {
    return createPortal(
      <div className="fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 max-sm:bottom-3 max-sm:w-[calc(100%-1.5rem)]">
        <div className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-2.5 shadow-lg">
          <span className="text-sm text-[var(--color-text)]">Du har ett pågående prov.</span>
          <a
            href={`/prov-elev/${pagaendePassId}`}
            title="Gå tillbaka till provet och fortsätt skriva"
            className="ml-auto shrink-0 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Fortsätt provet
          </a>
        </div>
      </div>,
      document.body,
    );
  }

  return null;
}
